/** Procurement rounding helpers — mirrors `client/src/lib/purchasePlanning.ts` using live product + OTB fields. */

import { formatCurrencyUsdFixedTwo, formatCurrencyUsdTwoOrFour } from '../helpers/common';

export const PURCHASE_PLANNING_EXCEPTION_TYPES = [
  'missing-supplier',
  'missing-unit-cost',
  'container-group-missing-capacity',
  'container-capacity-without-group',
  'container-capacity-mismatch',
  'needs-review',
  'underfilled-container',
  'allocation-residual',
];

const EXCEPTION_LABELS = {
  'missing-supplier': 'Supplier missing',
  'missing-unit-cost': 'Unit cost missing',
  'container-group-missing-capacity': 'Container group missing capacity',
  'container-capacity-without-group': 'Container capacity missing group',
  'container-capacity-mismatch': 'Container capacity mismatch',
  'needs-review': 'Product marked needs review',
  'underfilled-container': 'Material container fill required',
  'allocation-residual': 'Residual fill allocation',
};

function roundCurrency(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function normalizePositiveWholeNumber(value) {
  const numeric = Number(value ?? 0);
  if (!Number.isFinite(numeric) || numeric <= 0) return 0;
  return Math.round(numeric);
}

function roundUpToMultiple(value, multiple) {
  if (value <= 0) return 0;
  if (multiple <= 1) return Math.ceil(value);
  return Math.ceil(value / multiple) * multiple;
}

function toMonthLabel(isoDate) {
  const [year, month] = String(isoDate || '').split('-');
  const monthNumber = Number(month);
  if (!year || !monthNumber) return 'Unscheduled';
  const monthName =
    ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][
      monthNumber - 1
    ] ?? 'Unscheduled';
  return `${monthName} ${year}`;
}

function buildGroupKey(supplier, containerGroupId) {
  const normalizedSupplier = String(supplier || '').trim();
  const normalizedGroupId =
    containerGroupId != null && String(containerGroupId).trim() !== ''
      ? String(containerGroupId).trim()
      : '';
  if (!normalizedSupplier || !normalizedGroupId) return '';
  return `${normalizedSupplier}::${normalizedGroupId}`;
}

function getRoundingType(input) {
  const parts = [];
  if (input.moqBuyUnits > input.otbUnits) parts.push('MOQ');
  if (input.roundedUnits > Math.max(input.otbUnits, input.moqBuyUnits)) parts.push('Carton');
  if (input.fillAllocatedUnits > 0) parts.push('Container');
  return parts.length > 0 ? parts.join(' + ') : 'None';
}

export function describePurchasePlanningExceptions(exceptionTypes) {
  if (!exceptionTypes?.length) return 'No exceptions';
  return exceptionTypes.map(type => EXCEPTION_LABELS[type] || type).join(' · ');
}

function computeMoqBuyUnits(otbUnits, moq) {
  if (otbUnits <= 0 || moq <= 0) return 0;
  return ceilToMultiple(Math.max(otbUnits, moq), moq);
}

function computeBaseBuyUnits(otbUnits, moq) {
  return otbUnits > moq ? otbUnits : moq;
}

function ceilToMultiple(value, multiple) {
  if (!Number.isFinite(value) || !Number.isFinite(multiple) || multiple <= 0 || value <= 0) {
    return 0;
  }
  return Math.ceil(value / multiple) * multiple;
}

function computeGroupMoqBuyTotal(groupRows) {
  return groupRows.reduce((sum, row) => sum + row.moqBuyUnits, 0);
}

/** Container plan from group base buy — mirrors OTB `applyOpenToBuyGroupBaseBuy`. */
function computeGroupContainerPlan(groupBaseBuy, containerQty) {
  const buy = Math.max(0, Math.round(Number(groupBaseBuy ?? 0) || 0));
  const qty = normalizePositiveWholeNumber(containerQty);
  if (buy <= 0 || qty <= 0) {
    return { containers: 0, containerUnits: 0, fillGap: 0 };
  }
  // Containers = CEIL(Group Base Buy ÷ Container Qty); never round down partial containers.
  const containers = Math.ceil(buy / qty);
  const containerUnits = containers * qty;
  return {
    containers,
    containerUnits,
    fillGap: Math.max(containerUnits - buy, 0),
  };
}

function hasGroupContainerQtyMismatch(groupRows) {
  const qtys = new Set(
    (groupRows || [])
      .map(row => normalizePositiveWholeNumber(row.containerQty))
      .filter(value => value > 0)
  );
  return qtys.size > 1;
}

/** Canonical container qty for a pool — mode of SKU values so one outlier does not zero the plan. */
function resolveGroupContainerQty(groupRows) {
  const counts = new Map();
  for (const row of groupRows || []) {
    const qty = normalizePositiveWholeNumber(row.containerQty);
    if (qty <= 0) continue;
    counts.set(qty, (counts.get(qty) ?? 0) + 1);
  }
  if (counts.size === 0) return 0;

  let bestQty = 0;
  let bestCount = -1;
  for (const [qty, count] of counts) {
    if (count > bestCount || (count === bestCount && qty > bestQty)) {
      bestQty = qty;
      bestCount = count;
    }
  }
  return bestQty;
}

/** Remainder rank for proportional fill ties: (baseBuy × divisor) mod groupBaseBuy. */
function getProportionalRemainderRank(baseBuyUnits, groupBaseBuy, divisor) {
  const baseBuy = Math.max(0, Math.round(Number(baseBuyUnits ?? 0) || 0));
  const groupBuy = Math.max(0, Math.round(Number(groupBaseBuy ?? 0) || 0));
  const divisorTotal = Math.max(0, Math.round(Number(divisor ?? 0) || 0));
  if (groupBuy <= 0 || divisorTotal <= 0) return 0;
  return (baseBuy * divisorTotal) % groupBuy;
}

/** Proportional fill allocation by base buy — mirrors OTB `allocateOpenToBuyFillAllocatedUnits`. */
function allocateFillAllocatedUnits(groupRows, groupFill, groupBaseBuy) {
  const allocations = new Map();
  for (const row of groupRows || []) {
    allocations.set(String(row.id), 0);
  }
  if (groupFill <= 0 || groupBaseBuy <= 0) {
    return allocations;
  }

  const eligibleRows = (groupRows || []).filter(row => row.otbUnits > 0 && row.baseBuyUnits > 0);
  let allocatedSum = 0;
  for (const row of eligibleRows) {
    const baseBuy = Number(row.baseBuyUnits ?? 0) || 0;
    const floorAlloc = Math.floor((baseBuy * groupFill) / groupBaseBuy);
    allocations.set(String(row.id), floorAlloc);
    allocatedSum += floorAlloc;
  }

  let remainder = groupFill - allocatedSum;
  if (remainder > 0) {
    const sorted = [...eligibleRows].sort((left, right) => {
      const rankLeft = getProportionalRemainderRank(left.baseBuyUnits, groupBaseBuy, groupFill);
      const rankRight = getProportionalRemainderRank(right.baseBuyUnits, groupBaseBuy, groupFill);
      if (rankRight !== rankLeft) return rankRight - rankLeft;
      return String(left.supplySku ?? '').localeCompare(String(right.supplySku ?? ''));
    });
    for (const row of sorted) {
      if (remainder <= 0) break;
      const id = String(row.id);
      allocations.set(id, (allocations.get(id) ?? 0) + 1);
      remainder -= 1;
    }
  }

  return allocations;
}

function mapContainerOptimizationGroupProduct(row) {
  return {
    id: row.id,
    supplySku: row.supplySku,
    productName: row.productName,
    remainingAnnualDemand: row.remainingAnnualDemand,
    tableOtbUnits: row.tableOtbUnits,
    otbUnits: row.otbUnits,
    moq: row.moq,
    moqBuyUnits: row.moqBuyUnits,
    baseBuyUnits: row.baseBuyUnits,
    fillUnits: row.fillUnits ?? 0,
    fillAllocatedUnits: row.fillAllocatedUnits ?? row.fillUnits ?? 0,
    adjustedRoundedUnits: row.adjustedRoundedUnits,
    unitCost: row.unitCost,
    cashRequired: row.cashRequired,
  };
}

function computeContainerOptimizationGroupMetrics(groupRows) {
  const containerQtyMismatch = hasGroupContainerQtyMismatch(groupRows);
  const containerQty = resolveGroupContainerQty(groupRows);
  const groupBaseBuy = groupRows.reduce((sum, row) => sum + row.baseBuyUnits, 0);
  const totalMoqBuy = computeGroupMoqBuyTotal(groupRows);
  const { containers, containerUnits, fillGap } = computeGroupContainerPlan(groupBaseBuy, containerQty);
  // Group-level truth: Fill = Container Units − Group Base Buy; Adjusted = Container Units.
  const fillUnits = fillGap;
  const adjustedGroupUnits = containerUnits;
  const preFillUtilization = containerUnits > 0 ? groupBaseBuy / containerUnits : 1;
  const utilization = containerUnits > 0 ? 1 : 1;

  return {
    containerQty,
    containerQtyMismatch,
    groupBaseBuy,
    totalMoqBuy,
    fillGap,
    containers,
    containerUnits,
    fillUnits,
    adjustedGroupUnits,
    preFillUtilization,
    utilization,
  };
}

/**
 * @param {Array<object>} records OTB-style rows with procurement fields from Product.
 */
export function buildPurchasePlanningRows(records) {
  const scopedRows = (records || [])
    .filter(
      record =>
        record.orderEligible ||
        normalizePositiveWholeNumber(record.remainingAnnualDemand ?? record.otbUnits) > 0 ||
        normalizePositiveWholeNumber(record.unitsToOrder) > 0
    )
    .map(record => {
      const supplier = String(record.supplier || '').trim() || 'Supplier pending';
      const productName = String(record.productName || '').trim();
      const remainingAnnualDemand = normalizePositiveWholeNumber(
        record.remainingAnnualDemand ?? record.otbUnits
      );
      const tableOtbUnits = normalizePositiveWholeNumber(record.tableOtbUnits);
      const otbUnits = remainingAnnualDemand;
      const moq = normalizePositiveWholeNumber(record.moq);
      const cartonQty = normalizePositiveWholeNumber(record.cartonQty);
      const containerQty = normalizePositiveWholeNumber(record.containerQty);
      const containerGroupIdRaw =
        record.containerGroupId != null && String(record.containerGroupId).trim() !== ''
          ? Number(record.containerGroupId)
          : null;
      const containerGroupId = Number.isFinite(containerGroupIdRaw) ? containerGroupIdRaw : null;
      const containerGroup = String(record.containerGroup || '').trim();
      const groupKey = buildGroupKey(supplier, containerGroupId);
      const hasContainerPooling = Boolean(groupKey && containerQty > 0);
      const moqBuyUnits = computeMoqBuyUnits(otbUnits, moq);
      const baseBuyUnits = computeBaseBuyUnits(otbUnits, moq);
      const roundedUnits = moqBuyUnits;
      const nextPoRequiredDate = record.nextPoRequiredDate || '';
      const unitCost = Number(record.unitCost ?? 0);
      const exceptionTypes = [];

      if (!String(record.supplier || '').trim()) {
        exceptionTypes.push('missing-supplier');
      }
      if (containerGroupId && containerQty <= 0) {
        exceptionTypes.push('container-group-missing-capacity');
      }
      if (!containerGroupId && containerQty > 0) {
        exceptionTypes.push('container-capacity-without-group');
      }
      if (record.needsReview) {
        exceptionTypes.push('needs-review');
      }
      if (unitCost <= 0) {
        exceptionTypes.push('missing-unit-cost');
      }

      return {
        id: String(record.id),
        supplySku: record.supplySku,
        productName,
        canonicalProductName: productName,
        category: record.category || '',
        subCategory: record.subCategory || '',
        status: record.status || '',
        supplier,
        containerGroupId,
        containerGroup,
        groupKey,
        leadTimeDays: normalizePositiveWholeNumber(record.leadTimeDays),
        remainingAnnualDemand,
        tableOtbUnits,
        otbUnits,
        moq,
        cartonQty,
        containerQty,
        unitCost,
        nextPoRequiredDate,
        month: nextPoRequiredDate ? toMonthLabel(nextPoRequiredDate) : 'Unscheduled',
        hasContainerPooling,
        moqBuyUnits,
        roundedUnits,
        baseBuyUnits,
        groupBaseBuy: baseBuyUnits,
        containers: 0,
        containerUnits: 0,
        fillUnits: 0,
        fillAllocatedUnits: 0,
        adjustedRoundedUnits: otbUnits <= 0 ? 0 : moqBuyUnits,
        roundingDelta: (otbUnits <= 0 ? 0 : moqBuyUnits) - otbUnits,
        roundingType: getRoundingType({ otbUnits, moqBuyUnits, roundedUnits, fillAllocatedUnits: 0 }),
        cashRequired: roundCurrency((otbUnits <= 0 ? 0 : moqBuyUnits) * unitCost),
        groupUtilization: 1,
        preFillUtilization: 1,
        procurementEligible: Boolean(record.orderEligible),
        needsReview: Boolean(record.needsReview),
        exceptionTypes,
        exceptionSummary: describePurchasePlanningExceptions(exceptionTypes),
      };
    });

  const groups = new Map();
  scopedRows.forEach(row => {
    if (!row.hasContainerPooling) return;
    const groupRows = groups.get(row.groupKey);
    if (groupRows) {
      groupRows.push(row);
      return;
    }
    groups.set(row.groupKey, [row]);
  });

  groups.forEach(groupRows => {
    const containerQtyMismatch = hasGroupContainerQtyMismatch(groupRows);
    const containerQty = resolveGroupContainerQty(groupRows);
    const groupBaseBuy = groupRows.reduce((sum, row) => sum + row.baseBuyUnits, 0);
    const { containers, containerUnits, fillGap } = computeGroupContainerPlan(groupBaseBuy, containerQty);
    const preFillUtilization = containerUnits > 0 ? groupBaseBuy / containerUnits : 1;
    const fillAllocatedById = allocateFillAllocatedUnits(groupRows, fillGap, groupBaseBuy);

    groupRows.forEach(row => {
      const fillAllocatedUnits = fillAllocatedById.get(String(row.id)) ?? 0;
      row.groupBaseBuy = groupBaseBuy;
      row.containers = containers;
      row.containerUnits = containerUnits;
      row.fillUnits = fillAllocatedUnits;
      row.preFillUtilization = preFillUtilization;
      row.fillAllocatedUnits = fillAllocatedUnits;
      row.adjustedRoundedUnits = row.otbUnits <= 0 ? 0 : row.baseBuyUnits + fillAllocatedUnits;
      row.roundingDelta = row.adjustedRoundedUnits - row.otbUnits;
      row.roundingType = getRoundingType({
        otbUnits: row.otbUnits,
        moqBuyUnits: row.moqBuyUnits,
        roundedUnits: row.roundedUnits,
        fillAllocatedUnits: row.fillAllocatedUnits,
      });
      row.cashRequired = roundCurrency(row.adjustedRoundedUnits * row.unitCost);
      row.groupUtilization = 1;

      if (containerQtyMismatch) {
        row.exceptionTypes = Array.from(new Set([...row.exceptionTypes, 'container-capacity-mismatch']));
      }
      if (fillGap > 0) {
        row.exceptionTypes = Array.from(new Set([...row.exceptionTypes, 'underfilled-container']));
      }
      row.exceptionSummary = describePurchasePlanningExceptions(row.exceptionTypes);
    });

    const allocatedFillUnits = groupRows.reduce((sum, row) => sum + (row.fillAllocatedUnits ?? 0), 0);
    groupRows.forEach(row => {
      row.groupUtilization =
        containerUnits > 0 ? Math.min((groupBaseBuy + allocatedFillUnits) / containerUnits, 1) : 1;
    });
    if (fillGap > 0 && allocatedFillUnits !== fillGap) {
      groupRows.forEach(row => {
        row.exceptionTypes = Array.from(new Set([...row.exceptionTypes, 'allocation-residual']));
        row.exceptionSummary = describePurchasePlanningExceptions(row.exceptionTypes);
      });
    }
  });

  return scopedRows.sort(
    (left, right) =>
      right.adjustedRoundedUnits - left.adjustedRoundedUnits ||
      left.supplier.localeCompare(right.supplier) ||
      left.supplySku.localeCompare(right.supplySku)
  );
}

export function buildContainerOptimizationGroups(rows) {
  const grouped = new Map();

  (rows || [])
    .filter(row => row.hasContainerPooling)
    .forEach(row => {
      const existing = grouped.get(row.groupKey);
      if (existing) {
        existing.push(row);
        return;
      }
      grouped.set(row.groupKey, [row]);
    });

  return Array.from(grouped.entries())
    .map(([groupKey, groupRows]) => {
      const firstRow = groupRows[0];
      const metrics = computeContainerOptimizationGroupMetrics(groupRows);
      const totalCashRequired = roundCurrency(groupRows.reduce((sum, row) => sum + row.cashRequired, 0));
      const earliestPoDate =
        groupRows
          .map(row => row.nextPoRequiredDate)
          .filter(Boolean)
          .sort((left, right) => left.localeCompare(right))[0] ?? '';
      const exceptionTypes = Array.from(new Set(groupRows.flatMap(row => row.exceptionTypes))).sort();

      const products = groupRows
        .map(mapContainerOptimizationGroupProduct)
        .sort((left, right) => left.supplySku.localeCompare(right.supplySku));

      return {
        id: groupKey,
        supplier: firstRow?.supplier ?? 'Supplier pending',
        containerGroupId: firstRow?.containerGroupId ?? null,
        containerGroup: firstRow?.containerGroup || 'Ungrouped',
        containerQty: metrics.containerQty,
        skuCount: groupRows.length,
        groupBaseBuy: metrics.groupBaseBuy,
        totalMoqBuy: metrics.totalMoqBuy,
        fillGap: metrics.fillGap,
        adjustedGroupUnits: metrics.adjustedGroupUnits,
        fillUnits: metrics.fillUnits,
        containers: metrics.containers,
        containerUnits: metrics.containerUnits,
        preFillUtilization: metrics.preFillUtilization,
        utilization: metrics.utilization,
        totalCashRequired,
        earliestPoDate,
        months: Array.from(new Set(groupRows.map(row => row.month))).sort(),
        exceptionTypes,
        exceptionSummary: describePurchasePlanningExceptions(exceptionTypes),
        products,
      };
    })
    .sort(
      (left, right) =>
        right.fillUnits - left.fillUnits ||
        right.adjustedGroupUnits - left.adjustedGroupUnits ||
        left.supplier.localeCompare(right.supplier) ||
        left.containerGroup.localeCompare(right.containerGroup)
    );
}

export function formatPurchasePlanningNumber(value) {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value || 0);
}

/** Unit BOM costs — up to 4 decimals when needed (e.g. $0.02). */
export function formatPurchasePlanningCurrency(value) {
  return formatCurrencyUsdTwoOrFour(value || 0);
}

/** Cash required / exposure totals — always 2 decimal places. */
export function formatPurchasePlanningCashRequired(value) {
  return formatCurrencyUsdFixedTwo(value || 0);
}

export function formatPurchasePlanningPercent(value) {
  return `${((value || 0) * 100).toFixed(0)}%`;
}

export function getPurchaseRoundingExceptionTone(exceptionTypes) {
  if (
    exceptionTypes.includes('missing-supplier') ||
    exceptionTypes.includes('missing-unit-cost')
  ) {
    return 'critical';
  }
  if (
    exceptionTypes.includes('container-capacity-mismatch') ||
    exceptionTypes.includes('allocation-residual')
  ) {
    return 'warning';
  }
  if (exceptionTypes.length > 0) return 'attention';
  return 'ok';
}

function exceptionToneClasses(tone) {
  if (tone === 'critical') return 'bg-red-100 text-red-700';
  if (tone === 'warning') return 'bg-amber-100 text-amber-700';
  if (tone === 'attention') return 'bg-slate-100 text-slate-700';
  return 'bg-emerald-100 text-emerald-700';
}

export function getPurchaseRoundingExceptionBadgeClass(exceptionTypes) {
  return exceptionToneClasses(getPurchaseRoundingExceptionTone(exceptionTypes));
}

export function matchesPurchaseRoundingFilter(row, roundingFilter) {
  if (roundingFilter === 'all') return true;
  if (roundingFilter === 'none') return row.roundingType === 'None';
  return row.roundingType.toLowerCase().includes(roundingFilter);
}

export function matchesPurchaseRoundingExceptionFilter(row, exceptionFilter) {
  if (exceptionFilter === 'all') return true;
  if (exceptionFilter === 'exceptions') return row.exceptionTypes.length > 0;
  return row.exceptionTypes.includes(exceptionFilter);
}

export function filterPurchaseRoundingRows(
  rows,
  { search = '', exceptionFilter = 'all', groupFilter = 'all', roundingFilter = 'all' } = {}
) {
  const normalizedSearch = search.trim().toLowerCase();
  return (rows || []).filter(row => {
    const matchesSearch =
      !normalizedSearch ||
      row.supplySku.toLowerCase().includes(normalizedSearch) ||
      row.productName.toLowerCase().includes(normalizedSearch) ||
      row.supplier.toLowerCase().includes(normalizedSearch) ||
      row.containerGroup.toLowerCase().includes(normalizedSearch);

    const matchesGroup =
      groupFilter === 'all'
        ? true
        : groupFilter === 'ungrouped'
          ? !row.containerGroupId
          : row.containerGroup === groupFilter;

    return (
      matchesSearch &&
      matchesPurchaseRoundingExceptionFilter(row, exceptionFilter) &&
      matchesGroup &&
      matchesPurchaseRoundingFilter(row, roundingFilter)
    );
  });
}

export function summarizePurchaseRoundingRows(filteredRows) {
  return {
    skuCount: filteredRows.length,
    totalOtbUnits: filteredRows.reduce((sum, row) => sum + row.otbUnits, 0),
    totalBaseBuyUnits: filteredRows.reduce((sum, row) => sum + row.baseBuyUnits, 0),
    totalAdjustedRoundedUnits: filteredRows.reduce((sum, row) => sum + row.adjustedRoundedUnits, 0),
    totalRoundingDelta: filteredRows.reduce((sum, row) => sum + row.roundingDelta, 0),
    pooledRows: filteredRows.filter(row => row.hasContainerPooling).length,
    exceptionCount: filteredRows.filter(row => row.exceptionTypes.length > 0).length,
  };
}

export function buildExceptionExportRows(rows) {
  return (rows || [])
    .filter(row => row.exceptionTypes.length > 0)
    .map(row => [
      row.supplySku,
      row.productName,
      row.supplier,
      row.containerGroup || 'Ungrouped',
      row.otbUnits,
      row.baseBuyUnits,
      row.adjustedRoundedUnits,
      row.roundingType,
      row.exceptionSummary,
      row.exceptionTypes.includes('container-group-missing-capacity')
        ? 'Populate container_qty for the grouped supplier pool before relying on pooled rounding.'
        : row.exceptionTypes.includes('container-capacity-mismatch')
          ? 'Review container_qty consistency across all SKUs in the supplier pool.'
          : row.exceptionTypes.includes('missing-unit-cost')
            ? 'Backfill BOM product cost so downstream capital planning is complete.'
            : 'Review procurement metadata and pooled fill assumptions before execution.',
    ]);
}

export function getContainerOptimizationGroupTone(group) {
  if (
    group.exceptionTypes.includes('container-capacity-mismatch') ||
    group.exceptionTypes.includes('allocation-residual')
  ) {
    return 'critical';
  }
  if (group.fillUnits > 0) return 'warning';
  return 'ok';
}

export function getContainerOptimizationGroupBadgeClass(group) {
  const tone = getContainerOptimizationGroupTone(group);
  return exceptionToneClasses(tone);
}

export function getContainerOptimizationGroupStatusLabel(group) {
  if (group.exceptionSummary && group.exceptionTypes.length > 0) {
    return group.exceptionSummary;
  }
  return group.fillUnits > 0 ? 'Material fill required' : 'Healthy container pool';
}

export function matchesContainerOptimizationGroupFilter(group, groupFilter) {
  if (groupFilter === 'all') return true;
  if (groupFilter === 'exceptions') return group.exceptionTypes.length > 0;
  if (groupFilter === 'high-fill') return group.fillUnits > 0;
  if (groupFilter === 'healthy') return group.fillUnits === 0;
  return true;
}

export function filterContainerOptimizationGroups(
  groups,
  { search = '', groupFilter = 'all', supplierFilter = 'all' } = {}
) {
  const normalizedSearch = search.trim().toLowerCase();
  return (groups || []).filter(group => {
    const matchesSearch =
      !normalizedSearch ||
      group.supplier.toLowerCase().includes(normalizedSearch) ||
      group.containerGroup.toLowerCase().includes(normalizedSearch) ||
      group.months.join(' ').toLowerCase().includes(normalizedSearch);

    const matchesSupplier = supplierFilter === 'all' || group.supplier === supplierFilter;

    return (
      matchesSearch &&
      matchesSupplier &&
      matchesContainerOptimizationGroupFilter(group, groupFilter)
    );
  });
}

export function summarizeContainerOptimizationGroups(filteredGroups) {
  return {
    groupCount: filteredGroups.length,
    totalSkuCount: filteredGroups.reduce((sum, group) => sum + group.skuCount, 0),
    totalBaseBuy: filteredGroups.reduce((sum, group) => sum + group.groupBaseBuy, 0),
    totalAdjusted: filteredGroups.reduce((sum, group) => sum + group.adjustedGroupUnits, 0),
    totalFill: filteredGroups.reduce((sum, group) => sum + group.fillUnits, 0),
    totalCash: filteredGroups.reduce((sum, group) => sum + group.totalCashRequired, 0),
    exceptionCount: filteredGroups.filter(group => group.exceptionTypes.length > 0).length,
  };
}

export function scopeInventoryCapitalPlanRows(rows) {
  return (rows || []).filter(
    row => row.adjustedRoundedUnits > 0 || row.cashRequired > 0
  );
}

export function buildInventoryCapitalMonthSummaries(rows) {
  const grouped = new Map();
  (rows || []).forEach(row => {
    const key = row.orderMonth || row.month || 'Unscheduled';
    const current = grouped.get(key);
    if (current) {
      current.cashRequired += row.cashRequired;
      current.adjustedRoundedUnits += row.adjustedRoundedUnits;
      current.skuCount += 1;
      return;
    }
    grouped.set(key, {
      month: key,
      orderMonthKey: row.orderMonthKey || '',
      cashRequired: row.cashRequired,
      adjustedRoundedUnits: row.adjustedRoundedUnits,
      skuCount: 1,
    });
  });

  return Array.from(grouped.values()).sort((left, right) => {
    if (left.orderMonthKey && right.orderMonthKey) {
      const byWindow = left.orderMonthKey.localeCompare(right.orderMonthKey);
      if (byWindow !== 0) return byWindow;
    }
    return (
      right.cashRequired - left.cashRequired || left.month.localeCompare(right.month)
    );
  });
}

export function buildInventoryCapitalCategorySummaries(rows) {
  const grouped = new Map();
  (rows || []).forEach(row => {
    const key = row.category || 'Unassigned';
    const current = grouped.get(key);
    if (current) {
      current.cashRequired += row.cashRequired;
      current.adjustedRoundedUnits += row.adjustedRoundedUnits;
      current.skuCount += 1;
      return;
    }
    grouped.set(key, {
      category: key,
      cashRequired: row.cashRequired,
      adjustedRoundedUnits: row.adjustedRoundedUnits,
      skuCount: 1,
    });
  });
  return Array.from(grouped.values()).sort(
    (left, right) =>
      right.cashRequired - left.cashRequired || left.category.localeCompare(right.category)
  );
}

export function matchesInventoryCapitalExceptionFilter(row, exceptionFilter) {
  if (exceptionFilter === 'all') return true;
  if (exceptionFilter === 'exceptions') {
    return row.exceptionTypes.length > 0 || !row.nextPoRequiredDate;
  }
  if (exceptionFilter === 'missing-cost') return row.unitCost <= 0;
  if (exceptionFilter === 'unscheduled') return !row.nextPoRequiredDate;
  return true;
}

export function filterInventoryCapitalPlanRows(
  rows,
  { search = '', categoryFilter = 'all', monthFilter = 'all', exceptionFilter = 'all' } = {}
) {
  const normalizedSearch = search.trim().toLowerCase();
  return (rows || []).filter(row => {
    const matchesSearch =
      !normalizedSearch ||
      row.supplySku.toLowerCase().includes(normalizedSearch) ||
      row.productName.toLowerCase().includes(normalizedSearch) ||
      row.category.toLowerCase().includes(normalizedSearch) ||
      row.supplier.toLowerCase().includes(normalizedSearch);

    const matchesCategory = categoryFilter === 'all' || row.category === categoryFilter;
    const matchesMonth = monthFilter === 'all' || row.month === monthFilter;

    return (
      matchesSearch &&
      matchesCategory &&
      matchesMonth &&
      matchesInventoryCapitalExceptionFilter(row, exceptionFilter)
    );
  });
}

export function summarizeInventoryCapitalPlanRows(filteredRows) {
  const totalCashRequired = filteredRows.reduce((sum, row) => sum + row.cashRequired, 0);
  const totalAdjustedRoundedUnits = filteredRows.reduce(
    (sum, row) => sum + row.adjustedRoundedUnits,
    0
  );
  const missingCostCount = filteredRows.filter(row => row.unitCost <= 0).length;
  const unscheduledCount = filteredRows.filter(row => !row.nextPoRequiredDate).length;

  return {
    skuCount: filteredRows.length,
    totalCashRequired,
    totalAdjustedRoundedUnits,
    missingCostCount,
    unscheduledCount,
    averageUnitCost:
      totalAdjustedRoundedUnits > 0 ? totalCashRequired / totalAdjustedRoundedUnits : 0,
  };
}

export function downloadPurchaseRoundingCsv(filenameRoot, headers, rows) {
  const csv = [headers, ...rows]
    .map(row => row.map(value => `"${String(value).replaceAll('"', '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${String(filenameRoot)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'purchase-rounding-exceptions'}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
